import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, ListGroup, Badge, Form, Modal, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { FaCloudUploadAlt, FaTrash, FaEdit, FaCopy, FaCheckCircle, FaTimesCircle, FaFileAlt } from 'react-icons/fa';
import axios from 'axios';

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  version: string;
  status: 'active' | 'inactive';
  lastModified: string;
  usageCount: number;
  tags: string[];
}

const TemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadModalShow, setUploadModalShow] = useState(false);
  const [editModalShow, setEditModalShow] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Form state for new/edit template
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    tags: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      // Mock data for now - replace with actual API call
      const mockTemplates: Template[] = [
        {
          id: '1',
          name: 'Standard Purchase Agreement',
          description: 'Standard residential purchase agreement template',
          category: 'purchase',
          version: '2.1',
          status: 'active',
          lastModified: new Date().toISOString(),
          usageCount: 145,
          tags: ['residential', 'standard']
        },
        {
          id: '2',
          name: 'Listing Agreement - Exclusive',
          description: 'Exclusive right to sell listing agreement',
          category: 'listing',
          version: '1.8',
          status: 'active',
          lastModified: new Date().toISOString(),
          usageCount: 98,
          tags: ['listing', 'exclusive']
        }
      ];
      setTemplates(mockTemplates);
      setError(null);
    } catch (err) {
      setError('Failed to fetch templates');
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadTemplate = async (file: File) => {
    try {
      setUploadProgress(0);
      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 20;
        });
      }, 200);

      // Mock API call
      setTimeout(() => {
        fetchTemplates();
        setUploadModalShow(false);
        setFormData({ name: '', description: '', category: '', tags: '' });
        setUploadProgress(0);
      }, 1500);
    } catch (err) {
      setError('Failed to upload template');
      console.error('Upload error:', err);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      // Mock API call
      setTemplates(templates.filter(t => t.id !== templateId));
    } catch (err) {
      setError('Failed to delete template');
      console.error('Delete error:', err);
    }
  };

  const handleToggleStatus = async (template: Template) => {
    try {
      // Mock API call
      setTemplates(templates.map(t => 
        t.id === template.id 
          ? { ...t, status: t.status === 'active' ? 'inactive' : 'active' }
          : t
      ));
    } catch (err) {
      setError('Failed to update template status');
      console.error('Status update error:', err);
    }
  };

  const handleDuplicateTemplate = async (template: Template) => {
    try {
      // Mock API call
      const newTemplate = {
        ...template,
        id: Date.now().toString(),
        name: `${template.name} (Copy)`,
        lastModified: new Date().toISOString(),
        usageCount: 0
      };
      setTemplates([...templates, newTemplate]);
    } catch (err) {
      setError('Failed to duplicate template');
      console.error('Duplicate error:', err);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'purchase': 'success',
      'listing': 'primary',
      'disclosure': 'warning',
      'general': 'secondary'
    };
    return colors[category] || 'secondary';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-1">
                <FaFileAlt className="me-2" />
                Document Templates
              </h4>
              <p className="text-muted mb-0">
                Manage document templates for client transactions
              </p>
            </div>
            <Button variant="primary" onClick={() => setUploadModalShow(true)}>
              <FaCloudUploadAlt className="me-2" />
              Upload Template
            </Button>
          </div>

          <Row className="mb-3">
            <Col xs={6} md={3}>
              <Card bg="light">
                <Card.Body className="text-center">
                  <h5>{templates.length}</h5>
                  <small className="text-muted">Total Templates</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card bg="light">
                <Card.Body className="text-center">
                  <h5>{templates.filter(t => t.status === 'active').length}</h5>
                  <small className="text-muted">Active</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card bg="light">
                <Card.Body className="text-center">
                  <h5>{templates.filter(t => t.category === 'purchase').length}</h5>
                  <small className="text-muted">Purchase</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card bg="light">
                <Card.Body className="text-center">
                  <h5>{templates.filter(t => t.category === 'listing').length}</h5>
                  <small className="text-muted">Listing</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          {error}
        </Alert>
      )}

      <ListGroup>
        {templates.map((template) => (
          <ListGroup.Item key={template.id} className="mb-2">
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h6 className="mb-0">{template.name}</h6>
                  <Badge bg={getCategoryColor(template.category)}>
                    {template.category}
                  </Badge>
                  <Badge bg="light" text="dark">
                    v{template.version}
                  </Badge>
                  {template.status === 'active' ? (
                    <FaCheckCircle className="text-success" size={16} />
                  ) : (
                    <FaTimesCircle className="text-danger" size={16} />
                  )}
                </div>
                <p className="text-muted mb-2">{template.description}</p>
                <div className="d-flex align-items-center gap-3">
                  <small className="text-muted">
                    Last modified: {new Date(template.lastModified).toLocaleDateString()}
                  </small>
                  <small className="text-muted">
                    Used {template.usageCount} times
                  </small>
                </div>
                {template.tags.length > 0 && (
                  <div className="mt-2">
                    {template.tags.map((tag) => (
                      <Badge key={tag} bg="secondary" className="me-1">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleToggleStatus(template)}
                  title={template.status === 'active' ? 'Deactivate' : 'Activate'}
                >
                  {template.status === 'active' ? <FaTimesCircle /> : <FaCheckCircle />}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleDuplicateTemplate(template)}
                  title="Duplicate"
                >
                  <FaCopy />
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setSelectedTemplate(template);
                    setEditModalShow(true);
                  }}
                  title="Edit"
                >
                  <FaEdit />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDeleteTemplate(template.id)}
                  title="Delete"
                >
                  <FaTrash />
                </Button>
              </div>
            </div>
          </ListGroup.Item>
        ))}
      </ListGroup>

      {/* Upload Modal */}
      <Modal show={uploadModalShow} onHide={() => setUploadModalShow(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Upload New Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Template Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="">Select Category</option>
                <option value="purchase">Purchase Agreement</option>
                <option value="listing">Listing Agreement</option>
                <option value="disclosure">Disclosure</option>
                <option value="general">General</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tags (comma-separated)</Form.Label>
              <Form.Control
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Select File</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const files = (e.target as HTMLInputElement).files;
                  if (files && files[0]) {
                    handleUploadTemplate(files[0]);
                  }
                }}
              />
            </Form.Group>
            {uploadProgress > 0 && (
              <div className="mt-3">
                <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setUploadModalShow(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TemplateManager;